/**
 * Dataset Assistant component.
 */
import React, {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AssistantRuntimeProvider, type ThreadMessageLike } from "@assistant-ui/react";
import { useAgUiRuntime } from "@assistant-ui/react-ag-ui";
import { AssistantUnavailable } from "datasets/components/assistant/AssistantUnavailable";
import { createDatasetAssistantApi } from "datasets/components/assistant/api/DatasetAssistantApi";
import {
  loadDatasetAssistantState,
  updateDatasetAssistantState,
} from "datasets/components/assistant/datasetAssistantState";
import useDataset from "datasets/hooks/useDataset";
import { History, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { ShareIcon } from "~/assets/icons";

import { AgentEventProvider } from "~/components/assistant-v2/AgentEventProvider";
import { AnalysisAgentTools } from "~/components/assistant-v2/AnalysisAgentTools";
import { ReplayHttpAgent } from "~/components/assistant-v2/ReplayHttpAgent";
import { Thread } from "~/components/assistant-v2/Thread";
import {
  getActiveConversationId,
  setActiveConversationId,
} from "~/components/assistant-v2/active-conversations";
import { useRunningConversations } from "~/components/assistant-v2/running-conversations";
import { type AgUiState, useAgUiState } from "~/components/assistant-v2/useAgUiState";
import { useAssistantFeatures } from "~/components/assistant-v2/useAssistantFeatures";
import { useConversationName } from "~/components/assistant-v2/useConversationName";
import { useForwardAgentId } from "~/components/assistant-v2/useForwardAgentId";
import { useSkillSync } from "~/components/assistant-v2/useSkillSync";

import { AssistantProvider } from "~/components/assistant/AssistantContext";
import { ConversationSelector } from "~/components/assistant/ConversationSelector";
import { IconButton } from "~/components/assistant/PopoverButton";
import { ShareConversationModal } from "~/components/assistant/ShareConversationModal";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import { useAgentConnectors } from "~/components/assistant/hooks/useAgentConnectors";
import { useSystemAgentId } from "~/components/assistant/hooks/useSystemAgentId";
import type { ConversationStatus } from "~/components/assistant/types";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import type { PartialSkill, Skill, SkillFields } from "~/services/skills";
import { trackEvent } from "~/utils/monitoring";
import { ConversationSidebar } from "./ConversationSidebar";
import { useConversationManager } from "./useConversationManager";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

// The dataset assistant resolves the SYSTEM agent backing it (by name) up front.
// Every turn names that agent in `forwardedProps.agent_id` (useForwardAgentId),
// and the analysis-agent serves the turn as it. Every conversation here is served
// by that one agent, so the resolved id also drives skills and the connector
// picker. A failed lookup shows the assistant as unavailable: nothing can be sent
// without an agent to name.
// Keep in sync with conversations_types APPID_TO_SYSTEM_AGENT_NAME (DATASET_ASSISTANT).
const DATASET_ASSISTANT_AGENT_NAME = "legacy-dataset-assistant";
const api = createDatasetAssistantApi(VITE_ANALYSIS_AGENT_API);

// ---------------------------------------------------------------------------
// ConversationRuntime — one instance per visited conversation. Each is keyed
// on conversationId so it mounts/unmounts independently. Multiple instances
// can be alive simultaneously (hidden via CSS), allowing concurrent SSE
// streams to stay open across conversation switches.
// ---------------------------------------------------------------------------

const MAX_TABS = 5;

interface ConversationRuntimeProps {
  conversationId: string;
  agentId: string | null;
  datasetId: string;
  datasetTitle: string;
  api: AssistantApi;
  suggestions?: Array<{ title: string; prompt: string }>;
  updateConversationName: (conversationId: string, displayName: string) => void;
  skillsEnabled: boolean;
  skills: PartialSkill[];
  isLoadingSkills: boolean;
  createUserSkill: (fields: SkillFields) => Promise<void>;
  updateSkill: (skillId: string, fields: Partial<SkillFields>) => Promise<void>;
  deleteSkill: (skill: PartialSkill) => Promise<void>;
  getSkill: (skillId: string) => Promise<Skill>;
}

/**
 * Waits for /connect state and the SYSTEM agent's id, then renders the AG-UI
 * runtime for one conversation. Every turn names its agent, so the runtime does
 * not mount until there is one to name.
 */
const ConversationRuntime: FC<ConversationRuntimeProps> = ({ agentId, ...props }) => {
  const { initialState, initialMessages, status } = useAgUiState(
    VITE_ANALYSIS_AGENT_API,
    props.conversationId
  );

  if (!initialState || !agentId) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-6 text-gray-400 animate-spin" />
      </div>
    );
  }
  return (
    <ConversationRuntimeInner
      {...props}
      agentId={agentId}
      initialState={initialState}
      initialMessages={initialMessages}
      status={status}
    />
  );
};

const ConversationRuntimeInner: FC<
  Omit<ConversationRuntimeProps, "agentId"> & {
    agentId: string;
    initialState: AgUiState;
    initialMessages: ThreadMessageLike[] | null;
    status: ConversationStatus;
  }
> = ({
  conversationId,
  agentId,
  datasetId,
  datasetTitle,
  api,
  suggestions,
  updateConversationName,
  skillsEnabled,
  skills,
  isLoadingSkills,
  createUserSkill,
  updateSkill,
  deleteSkill,
  getSkill,
  initialState,
  initialMessages,
  status,
}) => {
  const datasetNames = useMemo(() => ({ [datasetId]: datasetTitle }), [datasetId, datasetTitle]);

  const { agentConnectors, selectedConnectorNames, toggleConnector } = useAgentConnectors(
    agentId,
    datasetId
  );

  const agent = useMemo(
    () =>
      new ReplayHttpAgent({
        url: `${VITE_ANALYSIS_AGENT_API}/conversations/${conversationId}/messages_agui`,
        headers: { Accept: "text/event-stream" },
        initialState,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- created once per mount
    []
  );

  const runtime = useAgUiRuntime({
    agent,
    showThinking: true,
    logger: {
      debug: (...args: any[]) => console.debug("[AG-UI]", ...args),
      error: (...args: any[]) => console.error("[AG-UI]", ...args),
    },
  });

  useConversationName(agent, updateConversationName);
  useSkillSync(agent, agentId);
  useForwardAgentId(runtime, agentId);

  return (
    <AssistantProvider api={api} conversationId={conversationId} contextId={datasetId}>
      <AssistantRuntimeProvider runtime={runtime}>
        <AgentEventProvider
          agent={agent}
          runtime={runtime}
          api={api}
          conversationId={conversationId}
          baseUrl={api.baseUrl}
          initialMessages={initialMessages}
          status={status}
        >
          <AnalysisAgentTools pipelineToolsEnabled={initialState.enable_pipeline_tools} />
          <Thread
            conversationId={conversationId}
            contextId={datasetId}
            suggestions={suggestions}
            skillsEnabled={skillsEnabled}
            skills={skills}
            isLoadingSkills={isLoadingSkills}
            onCreateUserSkill={createUserSkill}
            onUpdateSkill={updateSkill}
            onDeleteSkill={deleteSkill}
            onGetSkill={getSkill}
            agentConnectors={agentConnectors}
            selectedConnectorNames={selectedConnectorNames}
            onToggleConnector={toggleConnector}
            showToolSelector={true}
            placeholder={`Ask anything about ${datasetTitle}...`}
            datasetNames={datasetNames}
          />
        </AgentEventProvider>
      </AssistantRuntimeProvider>
    </AssistantProvider>
  );
};

// ---------------------------------------------------------------------------
// ConversationNameDisplay — inline editable conversation name in the toolbar.
// ---------------------------------------------------------------------------

interface ConversationNameDisplayProps {
  conversationId: string | null;
  displayName: string;
  onRename: (id: string, newName: string) => void;
  onDelete: (id: string, event: React.MouseEvent) => void;
}

function ConversationNameDisplay({
  conversationId,
  displayName,
  onRename,
  onDelete,
}: ConversationNameDisplayProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState(displayName);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isEditing) {
      setEditingName(displayName);
      const node = inputRef.current;
      if (node) {
        node.focus();
        node.select();
      }
    }
  }, [isEditing, displayName]);

  if (!conversationId) {
    return (
      <p className="text-sm font-medium text-foreground truncate cursor-default select-none">
        New Analysis
      </p>
    );
  }

  const handleSave = () => {
    const trimmed = editingName.trim();
    if (trimmed && trimmed !== displayName) {
      onRename(conversationId, trimmed);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        value={editingName}
        onChange={(e) => setEditingName(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        aria-label="Rename conversation"
        // Stable selector for smoke + integration tests. Don't remove. Don't rename.
        data-testid="rename-conversation-input"
        className="text-sm font-medium text-foreground bg-white border border-gray-300 rounded px-1.5 py-0.5 outline-none focus:border-blue-500 min-w-0 w-full max-w-xs"
      />
    );
  }

  return (
    <div className="group flex items-center gap-2 min-w-0">
      <p className="text-sm font-medium text-foreground truncate cursor-default select-none">
        {displayName}
      </p>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Conversation actions"
            className="opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 focus-visible:opacity-100 transition-opacity p-1 hover:bg-gray-200 data-[state=open]:bg-gray-200 rounded cursor-pointer"
          >
            <Pencil className="size-3.5 text-gray-600" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto min-w-[140px] p-1" align="start">
          <button
            type="button"
            onClick={() => {
              setPopoverOpen(false);
              setIsEditing(true);
            }}
            className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded cursor-pointer hover:bg-gray-100 whitespace-nowrap"
            // Stable selector for smoke + integration tests. Don't remove. Don't rename.
            data-testid="rename-menu-button"
          >
            <Pencil className="size-4 shrink-0" />
            Rename
          </button>
          <button
            type="button"
            onClick={(event) => {
              setPopoverOpen(false);
              onDelete(conversationId, event);
            }}
            className="flex items-center gap-2 w-full px-2 py-1.5 text-sm text-destructive hover:bg-destructive/10 rounded cursor-pointer whitespace-nowrap"
            // Stable selector for smoke + integration tests. Don't remove. Don't rename.
            data-testid="delete-menu-button"
          >
            <Trash2 className="size-4 shrink-0" />
            Delete
          </button>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DatasetAssistant — outer shell managing conversations, sidebar, header
// ---------------------------------------------------------------------------

export interface DatasetAssistantProps {
  datasetId: string;
  className?: string;
  headerActions?: ReactNode;
  compactMode?: boolean;
}

// @lat: [[frontend/ai-assistant#Dataset Assistant]]
export function DatasetAssistant({
  datasetId,
  className,
  headerActions,
  compactMode = false,
}: DatasetAssistantProps) {
  const { data: dataset } = useDataset(datasetId);
  const datasetTitle = dataset?.title ?? datasetId;
  const initialConversationId = getActiveConversationId(datasetId);

  const suggestions = useMemo(() => {
    const prompts = dataset?.samplePrompts
      ?.filter((p) => p.trim().length > 0)
      .map((p) => ({ title: p, prompt: p }));
    return prompts && prompts.length > 0 ? prompts : undefined;
  }, [dataset?.samplePrompts]);

  const conversationShortcuts = dataset?.conversationShortcuts ?? [];

  // Resolve the SYSTEM agent backing the dataset assistant up front so skills
  // and the connector picker know whose to fetch.
  const { agentId: systemAgentId, error: systemAgentError } = useSystemAgentId(
    DATASET_ASSISTANT_AGENT_NAME
  );
  useEffect(() => {
    if (systemAgentError) {
      console.error("Failed to resolve the dataset assistant's agent:", systemAgentError);
    }
  }, [systemAgentError]);

  const {
    conversations,
    currentConversationId,
    isLoading: isLoadingConversations,
    isCreating,
    loadError,
    createConversation,
    deleteConversation,
    selectConversation,
    renameConversation,
    updateConversationName,
  } = useConversationManager({
    api,
    datasetId,
    initialConversationId,
  });

  useEffect(() => {
    setActiveConversationId(datasetId, currentConversationId);
  }, [datasetId, currentConversationId]);

  const {
    skillsEnabled,
    skills,
    isLoadingSkills,
    createUserSkill,
    updateSkill,
    deleteSkill,
    getSkill,
  } = useAssistantFeatures({
    api,
    contextId: datasetId,
    agentId: systemAgentId,
    datasetId,
    enableSkills: true,
  });

  const runningIds = useRunningConversations();
  const isConversationStreaming = useCallback((id: string) => runningIds.has(id), [runningIds]);

  useEffect(() => {
    trackEvent("assistant_ui_opened", {
      dataset_id: datasetId,
      context: "dataset",
    });
    return () => {
      trackEvent("assistant_ui_closed", {
        dataset_id: datasetId,
        context: "dataset",
      });
    };
  }, [datasetId]);

  const handleNewChat = useCallback(async () => {
    trackEvent("assistant_new_chat_clicked", {
      dataset_id: datasetId,
      context: "dataset",
    });
    await createConversation();
  }, [datasetId, createConversation]);

  const handleConversationSelect = useCallback(
    (conversationId: string) => {
      if (conversationId === currentConversationId) {
        return;
      }
      trackEvent("assistant_conversation_selected", {
        dataset_id: datasetId,
        conversation_id: conversationId,
        context: "dataset",
      });
      selectConversation(conversationId);
    },
    [datasetId, currentConversationId, selectConversation]
  );

  // Track visited conversations so their runtimes stay alive (hidden) when
  // switching. Each gets its own AG-UI runtime, HttpAgent, and SSE stream.
  const [visitedIds, setVisitedIds] = useState<string[]>([]);

  useEffect(() => {
    if (!currentConversationId) return;
    setVisitedIds((prev) => {
      if (prev.includes(currentConversationId)) {
        // Move to end (most recently used)
        return [...prev.filter((id) => id !== currentConversationId), currentConversationId];
      }
      const next = [...prev, currentConversationId];
      return next.length > MAX_TABS ? next.slice(-MAX_TABS) : next;
    });
  }, [currentConversationId]);

  const handleDeleteConversation = useCallback(
    (conversationId: string, event: React.MouseEvent) => {
      trackEvent("assistant_conversation_deleted", {
        dataset_id: datasetId,
        conversation_id: conversationId,
        context: "dataset",
      });
      setVisitedIds((prev) => prev.filter((id) => id !== conversationId));
      deleteConversation(conversationId, event);
    },
    [datasetId, deleteConversation]
  );

  const handleRenameConversation = useCallback(
    (conversationId: string, displayName: string) => {
      trackEvent("assistant_conversation_renamed", {
        dataset_id: datasetId,
        conversation_id: conversationId,
        context: "dataset",
      });
      renameConversation(conversationId, displayName);
    },
    [datasetId, renameConversation]
  );

  const [isSidebarOpen, setIsSidebarOpen] = useState(
    () => loadDatasetAssistantState().v2SidebarOpen
  );
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => {
      const next = !prev;
      trackEvent("assistant_v2_sidebar_toggled", {
        dataset_id: datasetId,
        context: "dataset",
        open: next,
      });
      updateDatasetAssistantState({ v2SidebarOpen: next });
      return next;
    });
  }, [datasetId]);

  if (isLoadingConversations) {
    return (
      <div className={cn("flex h-full items-center justify-center bg-white", className)}>
        <Loader2 className="size-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (loadError || systemAgentError) {
    return <AssistantUnavailable className={className} />;
  }

  const currentDisplayName =
    conversations.find((c) => c.id === currentConversationId)?.display_name ?? "New Analysis";

  return (
    <div className={cn("flex flex-col h-full bg-white", className)}>
      <div className="flex flex-row items-center justify-between px-3 h-[52px] min-h-[52px] shrink-0 w-full border-b border-black/[0.12]">
        <div className="flex flex-row gap-1.5 items-center min-w-0">
          {compactMode ? (
            <ConversationSelector
              iconOnly
              conversations={conversations}
              currentConversationId={currentConversationId}
              conversationShortcuts={conversationShortcuts}
              isCreatingConversation={!currentConversationId}
              isStreaming={isConversationStreaming}
              onSelect={handleConversationSelect}
              onDelete={handleDeleteConversation}
              onRename={handleRenameConversation}
            />
          ) : (
            <Tooltip delayDuration={700}>
              <TooltipTrigger asChild>
                <IconButton
                  onClick={handleToggleSidebar}
                  variant="ghost"
                  aria-label="Toggle Chat History"
                >
                  <History className="size-4.5 text-gray-600" />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent>
                <p>Toggle Chat History</p>
              </TooltipContent>
            </Tooltip>
          )}
          <ConversationNameDisplay
            conversationId={currentConversationId}
            displayName={currentDisplayName}
            onRename={handleRenameConversation}
            onDelete={handleDeleteConversation}
          />
        </div>

        <div className="flex flex-row gap-1.5 items-center justify-end">
          <Tooltip delayDuration={700}>
            <TooltipTrigger asChild>
              <IconButton
                onClick={handleNewChat}
                disabled={isCreating}
                variant="ghost"
                aria-label="New Chat"
                // Stable selector for smoke + integration tests. Don't remove. Don't rename.
                data-testid="new-conversation-button"
              >
                <Plus className="size-4.5 text-gray-600" />
              </IconButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>New Chat</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip delayDuration={700}>
            <TooltipTrigger asChild>
              <IconButton
                onClick={() => setShareModalOpen(true)}
                variant="ghost"
                aria-label="Share Chat"
                disabled={!currentConversationId}
              >
                <ShareIcon className="size-4.5 text-gray-600" />
              </IconButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Share Chat</p>
            </TooltipContent>
          </Tooltip>
          {headerActions}
        </div>
      </div>

      <div className="flex flex-row flex-1 min-h-0">
        {!compactMode && isSidebarOpen && (
          <ConversationSidebar
            conversations={conversations}
            currentConversationId={currentConversationId}
            conversationShortcuts={conversationShortcuts}
            isStreaming={isConversationStreaming}
            onSelect={handleConversationSelect}
            onDelete={handleDeleteConversation}
            onRename={handleRenameConversation}
          />
        )}

        <div className="flex-1 min-h-0 min-w-0 relative">
          {visitedIds.map((id) => (
            <div
              key={id}
              className="absolute inset-0"
              data-active={id === currentConversationId ? "" : undefined}
              style={{
                visibility: id === currentConversationId ? "visible" : "hidden",
                pointerEvents: id === currentConversationId ? "auto" : "none",
              }}
            >
              <ConversationRuntime
                conversationId={id}
                agentId={systemAgentId}
                datasetId={datasetId}
                datasetTitle={datasetTitle}
                api={api}
                suggestions={suggestions}
                updateConversationName={updateConversationName}
                skillsEnabled={skillsEnabled}
                skills={skills}
                isLoadingSkills={isLoadingSkills}
                createUserSkill={createUserSkill}
                updateSkill={updateSkill}
                deleteSkill={deleteSkill}
                getSkill={getSkill}
              />
            </div>
          ))}
        </div>
      </div>

      {currentConversationId && (
        <ShareConversationModal
          open={shareModalOpen}
          onOpenChange={setShareModalOpen}
          conversationId={currentConversationId}
        />
      )}
    </div>
  );
}
